/**
 * Manages chat input state, submit (with optional text e.g. from chips), and
 * new-thread (clear messages + input, focus textarea). Uses latestInputRef so
 * handleSubmit stays stable across keystrokes.
 */
import type { UIMessage } from "ai";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface OutgoingMessage {
  role: "user";
  parts: Array<{ type: "text"; text: string }>;
}

/** AI SDK v6 native format for messages with file attachments. */
export interface FileAttachedMessage {
  text: string;
  files: FileList;
}

/** Union of both message shapes we pass to the SDK's sendMessage. */
export type SendableMessage = OutgoingMessage | FileAttachedMessage;

export interface UseChatSubmitDeps {
  isStreaming: boolean;
  sendMessage: (message: SendableMessage) => void;
  /** Same shape as `useChat` `setMessages` (clear thread uses `[]`). */
  setMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
  /** Reset the backend conversation so the next message creates a new one. */
  resetConversation?: () => void;
}

export interface UseChatSubmitReturn {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  files: FileList | undefined;
  setFiles: Dispatch<SetStateAction<FileList | undefined>>;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  handleSubmit: (text?: string) => void;
  handleNewThread: () => void;
}

const useChatSubmit = ({
  isStreaming,
  sendMessage,
  setMessages,
  resetConversation,
}: UseChatSubmitDeps): UseChatSubmitReturn => {
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<FileList | undefined>(undefined);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const latestInputRef = useRef(input);
  const latestFilesRef = useRef(files);

  useEffect(() => {
    latestInputRef.current = input;
  }, [input]);

  useEffect(() => {
    latestFilesRef.current = files;
  }, [files]);

  const handleSubmit = useCallback(
    (text?: string) => {
      const message = text ?? latestInputRef.current.trim();
      const pendingFiles = latestFilesRef.current;
      if ((!message && !pendingFiles) || isStreaming) return;
      setInput("");
      if (pendingFiles) {
        // AI SDK v6 native file-attached format — { text, files }.
        // Separate from { role, parts } because the SDK treats them as
        // distinct union branches; mixing both shapes causes type errors.
        sendMessage({ text: message || "", files: pendingFiles });
      } else {
        sendMessage({
          role: "user",
          parts: message ? [{ type: "text" as const, text: message }] : [],
        });
      }
      setFiles(undefined);
      inputRef.current?.blur();
    },
    [isStreaming, sendMessage],
  );

  const handleNewThread = useCallback(() => {
    resetConversation?.();
    setMessages([]);
    setInput("");
    setFiles(undefined);
    inputRef.current?.focus();
  }, [setMessages, resetConversation]);

  return {
    input,
    setInput,
    files,
    setFiles,
    inputRef,
    handleSubmit,
    handleNewThread,
  };
};

export default useChatSubmit;
